import { useCallback, useEffect, useMemo, useState } from 'react';
import { icons } from '../../components/ui/Icons';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardContent, CardHeader } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Select } from '../../components/ui/Select';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorState } from '../../components/ui/ErrorState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Permission } from '../../types/auth';
import { requestJson, getErrorMessage } from '../../lib/adminApi';

const PERMISSION_GROUPS = [
  'Dashboard',
  'Patienten',
  'Medizin',
  'Personal',
  'Organisation',
  'Dokumente',
  'Kommunikation',
  'Verwaltung',
  'System',
] as const;

type PermissionGroup = (typeof PERMISSION_GROUPS)[number];

const PERMISSION_CATEGORY_MAP: Record<string, PermissionGroup> = {
  Dashboard: 'Dashboard',
  'Patienten & Stationen': 'Patienten',
  Pflege: 'Patienten',
  Medizin: 'Medizin',
  Personal: 'Personal',
  Organisation: 'Organisation',
  Wissen: 'Dokumente',
  Kommunikation: 'Kommunikation',
  'Sicherheit & Notfall': 'Organisation',
  Betrieb: 'Organisation',
  Verwaltung: 'Verwaltung',
  System: 'System',
};

const GROUP_ICONS: Record<PermissionGroup, keyof typeof icons> = {
  Dashboard: 'LayoutDashboard',
  Patienten: 'Bed',
  Medizin: 'Stethoscope',
  Personal: 'Users',
  Organisation: 'Calendar',
  Dokumente: 'FileText',
  Kommunikation: 'MessageSquare',
  Verwaltung: 'Settings',
  System: 'Cpu',
};

function getPermissionGroup(permission: Permission): PermissionGroup {
  return PERMISSION_CATEGORY_MAP[permission.category] || 'System';
}

function groupPermissions(permissions: Permission[]): Record<PermissionGroup, Permission[]> {
  const groups = Object.fromEntries(
    PERMISSION_GROUPS.map((group) => [group, [] as Permission[]]),
  ) as Record<PermissionGroup, Permission[]>;

  permissions.forEach((permission) => {
    groups[getPermissionGroup(permission)].push(permission);
  });

  Object.values(groups).forEach((group) => {
    group.sort((a, b) => a.name.localeCompare(b.name, 'de-DE'));
  });

  return groups;
}

export function PermissionsCatalogPage() {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await requestJson<unknown[]>(`/permissions`);
      setPermissions(Array.isArray(data) ? data.map((item) => {
        const permission = item as Partial<Permission>;
        return {
          id: permission.id || '',
          key: permission.key || '',
          name: permission.name || permission.key || 'Unbenannte Berechtigung',
          category: permission.category || 'System',
          description: typeof permission.description === 'string' ? permission.description : null,
        };
      }) : []);
    } catch (error) {
      setError(getErrorMessage(error, 'Berechtigungen konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredPermissions = useMemo(() => {
    const query = search.trim().toLowerCase();
    return permissions.filter((permission) => {
      const matchesGroup = groupFilter === 'all' || getPermissionGroup(permission) === groupFilter;
      const searchable = `${permission.key} ${permission.name} ${permission.description || ''}`.toLowerCase();
      return matchesGroup && (!query || searchable.includes(query));
    });
  }, [permissions, search, groupFilter]);

  const groupedPermissions = useMemo(() => groupPermissions(filteredPermissions), [filteredPermissions]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const breadcrumbItems = [
    { label: 'Administration', href: '/admin' },
    { label: 'Benutzer & Zugriff', href: '/admin/access' },
    { label: 'Berechtigungen', current: true },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Berechtigungskatalog</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Alle aktiven Berechtigungen, gruppiert nach Dashboard, Fachbereichen, Verwaltung und System.
          </p>
        </div>
        <Button variant="outline" size="sm" leftIcon="RefreshCw" loading={loading} onClick={() => void fetchPermissions()}>
          Katalog aktualisieren
        </Button>
      </div>

      <Card className="border-neutral-200 dark:border-neutral-700">
        <CardContent className="space-y-4">
          {error && permissions.length === 0 ? (
            <ErrorState description={error} onRetry={() => void fetchPermissions()} />
          ) : error ? (
            <Alert variant="error" title="Berechtigungskatalog konnte nicht vollständig geladen werden" dismissible onDismiss={() => setError(null)}>
              {error}
            </Alert>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_14rem]">
            <Input
              aria-label="Berechtigungen durchsuchen"
              placeholder="Schlüssel, Name oder Beschreibung suchen ..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              leftIcon="Search"
            />
            <Select
              aria-label="Berechtigungsbereich filtern"
              value={groupFilter}
              onChange={(event) => setGroupFilter(event.target.value)}
              options={[
                { value: 'all', label: 'Alle Bereiche' },
                ...PERMISSION_GROUPS.map((group) => ({ value: group, label: group })),
              ]}
            />
          </div>

          {loading && permissions.length === 0 ? (
            <SkeletonTable rows={8} columns={3} />
          ) : filteredPermissions.length === 0 ? (
            <EmptyState
              icon="Key"
              title="Keine Berechtigungen gefunden"
              description="Der Katalog ist leer oder die Suche und Bereichsauswahl liefern keine Treffer."
              action={
                <Button variant="outline" size="sm" onClick={() => { setSearch(''); setGroupFilter('all'); }}>
                  Filter zurücksetzen
                </Button>
              }
            />
          ) : (
            <div className="space-y-4">
              {PERMISSION_GROUPS.map((group) => {
                const perms = groupedPermissions[group];
                if (perms.length === 0) {
                  return null;
                }
                const GroupIcon = icons[GROUP_ICONS[group]];
                return (
                  <Card key={group} className="border-neutral-200 dark:border-neutral-700">
                    <CardHeader className="mb-3 flex items-center gap-3 border-b border-neutral-200 pb-3 dark:border-neutral-700">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                        <GroupIcon className="h-4 w-4" aria-hidden="true" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-neutral-900 dark:text-white">{group}</h4>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">{perms.length} Einträge</p>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table
                        columns={[
                          {
                            key: 'key',
                            header: 'Schlüssel',
                            className: 'min-w-[220px]',
                            render: (permission: Permission) => (
                              <code className="break-all text-xs text-neutral-900 dark:text-neutral-100">{permission.key}</code>
                            ),
                          },
                          {
                            key: 'name',
                            header: 'Name',
                            className: 'min-w-[180px]',
                            render: (permission: Permission) => (
                              <span className="font-medium text-neutral-900 dark:text-white">{permission.name}</span>
                            ),
                          },
                          {
                            key: 'description',
                            header: 'Beschreibung',
                            render: (permission: Permission) => permission.description || '-',
                          },
                        ]}
                        data={perms}
                        keyExtractor={(permission) => permission.id}
                        emptyMessage="Keine Berechtigungen in diesem Bereich."
                      />
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}